import React from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import {
  CreateListResponse,
  useAccountLists,
} from '@/dashboard/AccountLists/useAccountLists/useAccountLists';
import { useAccountListsV2 } from '@/dashboard/AccountListsV2/hooks/useAccountListsV2';
import {
  LizanoMetaData,
  LizanoListInfo,
  LizanoListProperty,
} from '@/reports/sections/ListsSection/lists/lists';
import { AddReportToListV2AddressPayload } from '@/reports/types/serialized';
import { Icons } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { ReportActionButton } from './components';

export function SaveAction({
  reportPermaLink,
  reportType,
  reportData,
}: {
  reportPermaLink: string;
  reportType?: ValidReportTypes;
  reportData?: AddReportToListV2AddressPayload;
}) {
  const {
    data: list,
    createList,
    addReportToList,
    removeReportFromList,
  } = useAccountLists();
  const { addReportToListV2, bulkDeleteListProperty } = useAccountListsV2();
  const { trackEvent } = useAppConfigContext();

  const isPropertyReport = reportType && reportType === 'property';

  const isSavedReportsList =
    list && list.length > 0 && list[0]?.name === 'saved reports';

  let foundReportInSavedReports: LizanoMetaData | undefined;
  let reportFoundInListV2: LizanoListProperty | undefined;
  if (list && list.length > 0 && list[0]?.name === 'saved reports') {
    foundReportInSavedReports = list[0]?.list_reports?.find(
      (report) => report?.report?.permalink === reportPermaLink
    );

    if (isPropertyReport) {
      reportFoundInListV2 = (list?.[0]?.list_properties || []).find(
        (listProp: LizanoListProperty) =>
          listProp.permalink === reportPermaLink ||
          (reportData &&
            reportData.street.includes(listProp.street) &&
            reportData.city.includes(listProp.city) &&
            reportData.state.includes(listProp.state) &&
            reportData.zip5.includes(listProp.zip5))
      );
    }
  }

  const handleAddReportToListV2 = (
    data: LizanoListInfo | CreateListResponse | AddReportToListV2AddressPayload
  ) => {
    if (!list || !list[0] || !reportData) return;
    const typedData = data as CreateListResponse;
    const newCreatedListCode = typedData?.data?.list?.list_code;

    addReportToListV2.mutate({
      list_code: newCreatedListCode || list[0].list_code,
      property: reportData,
    });
  };

  const handleCreateAddReportToList = () => {
    trackEvent('save report', 'engagement click', 'rats component');
    createList.mutate(
      {
        name: 'saved reports',
        permalink: reportPermaLink,
      },
      {
        onSuccess: (response) => {
          if (isPropertyReport) {
            handleAddReportToListV2(response);
          }
        },
      }
    );
  };

  const handleRemoveReportFromListV2 = () => {
    if (!list || !list[0] || !reportFoundInListV2) return;

    bulkDeleteListProperty.mutate({
      list_code: list[0].list_code,
      propertyIDs: [reportFoundInListV2?.id.toString()],
    });
  };

  const handleRemoveFromList = () => {
    if (!list || !list[0] || !foundReportInSavedReports) return;

    removeReportFromList.mutate(
      {
        list_code: list[0].list_code,
        listReportId: foundReportInSavedReports.id,
      },
      {
        onSuccess: () => {
          if (isPropertyReport) {
            handleRemoveReportFromListV2();
          }
        },
      }
    );
  };

  const handleAddReportToList = () => {
    if (!list || !list[0]) return;

    trackEvent('save report', 'engagement click', 'rats component');
    addReportToList.mutate(
      {
        list_code: list[0].list_code,
        reportPermaLink,
      },
      {
        onSuccess: () => {
          if (isPropertyReport && reportData) {
            handleAddReportToListV2(reportData);
          }
        },
      }
    );
  };

  const handleAddRemoveAction = () => {
    if (!isSavedReportsList) {
      handleCreateAddReportToList();
      return;
    }
    if (foundReportInSavedReports) {
      handleRemoveFromList();
      return;
    }
    if (!list || !list[0]) return;
    if (isSavedReportsList) {
      handleAddReportToList();
    }
  };

  if (foundReportInSavedReports) {
    return (
      <ReportActionButton
        onClick={handleAddRemoveAction}
        variant="contained"
        className="save"
        sx={{ color: 'primary.contrastText', borderColor: 'info.main' }}
        startIcon={<Icons.FavoriteBorder fontSize="small" />}
        disableElevation
      >
        Saved
      </ReportActionButton>
    );
  }

  return (
    <ReportActionButton
      onClick={handleAddRemoveAction}
      variant="contained"
      className="save"
      color="info"
      startIcon={<Icons.FavoriteBorder fontSize="small" />}
      disableElevation
    >
      Save
    </ReportActionButton>
  );
}
